// pkce.util.ts
import SHA256 from 'crypto-js/sha256';
import encBase64 from 'crypto-js/enc-base64';

const VERIFIER_MIN = 43;
const VERIFIER_MAX = 128;
const PKCE_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

function assertLength(length: number) {
  if (length < VERIFIER_MIN || length > VERIFIER_MAX) {
    throw new Error(`PKCE verifier length must be ${VERIFIER_MIN}-${VERIFIER_MAX}. Got ${length}.`);
  }
}

function getCrypto(): Crypto {
  // Browser first, then Node
  // @ts-ignore
  if (typeof globalThis !== 'undefined' && globalThis.crypto) {
    return globalThis.crypto as Crypto;
  }

  throw new Error('Secure crypto not available (globalThis.crypto missing).');
}

function randomString(length: number, charset: string): string {
  assertLength(length);
  const arr = new Uint8Array(length);
  getCrypto().getRandomValues(arr);
  let out = '';

  for (let i = 0; i < length; i++) {
    out += charset[arr[i] % charset.length];
  }

  return out;
}

function base64UrlFromBytes(bytes: Uint8Array): string {
  // Prefer btoa; fall back to Buffer if not present (e.g., Node)
  let b64: string;
  if (typeof btoa === 'function') {
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    b64 = btoa(bin);
  } else {
    // Node
    // @ts-ignore
    b64 = Buffer.from(bytes).toString('base64');
  }
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * High-entropy PKCE code verifier.
 */
export function generateCodeVerifier(length = 96): string {
  return randomString(length, PKCE_CHARSET);
}

/**
 * URL-safe opaque state/nonce (not bound by 43–128 rule).
 */
export function generateState(length = 32): string {
  const cs = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const arr = new Uint8Array(length);
  getCrypto().getRandomValues(arr);
  let out = '';
  for (let i = 0; i < length; i++) out += cs[arr[i] % cs.length];
  return out;
}

export const generateNonce = generateState;

/**
 * Async code challenge using Web Crypto when available (preferred).
 */
export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  if (!codeVerifier || codeVerifier.length < VERIFIER_MIN || codeVerifier.length > VERIFIER_MAX) {
    throw new Error(`Invalid code_verifier length. Must be ${VERIFIER_MIN}-${VERIFIER_MAX}.`);
  }

  const cryptoObj = getCrypto();
  if (cryptoObj.subtle?.digest) {
    const data = new TextEncoder().encode(codeVerifier);
    const hash = await cryptoObj.subtle.digest('SHA-256', data);
    return base64UrlFromBytes(new Uint8Array(hash));
  }

  // Fallback to crypto-js
  const hash = SHA256(codeVerifier);
  const b64 = encBase64.stringify(hash);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Sync variant using crypto-js only. Use when async isn’t convenient.
 */
export function generateCodeChallengeSync(codeVerifier: string): string {
  if (!codeVerifier || codeVerifier.length < VERIFIER_MIN || codeVerifier.length > VERIFIER_MAX) {
    throw new Error(`Invalid code_verifier length. Must be ${VERIFIER_MIN}-${VERIFIER_MAX}.`);
  }
  const hash = SHA256(codeVerifier);
  const b64 = encBase64.stringify(hash);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * One-shot generator for { code_verifier, code_challenge, method }.
 */
export async function generatePkcePair(verifierLength = 96): Promise<{
  code_verifier: string;
  code_challenge: string;
  code_challenge_method: 'S256';
}> {
  const code_verifier = generateCodeVerifier(verifierLength);
  const code_challenge = await generateCodeChallenge(code_verifier);
  return { code_verifier, code_challenge, code_challenge_method: 'S256' };
}
